// @ts-nocheck
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabaseClient';

export type Settings = {
  theme: 'system'|'light'|'dark'|'contrast';
  density: 'comfortable'|'compact';
  layout: 'grid'|'list'|'map';
  reduce_motion: boolean;
  default_sort: 'distance'|'rating'|'recent'|'popular';
  radius_km: number;
  platforms: ('TikTok'|'Instagram'|'YouTube'|'Facebook')[];
  cuisines: string[];
  dietary_filters: string[]; // e.g., ['vegan','vegetarian','gluten_free']
  language: 'en'|'es';
  map_style: 'standard'|'satellite';
  directions_pref: 'google'|'apple'|'waze'|'uber';
  default_visibility: 'private'|'unlisted'|'public';
  share_options: { allow_comments?: boolean; allow_collaborators?: boolean; show_creator?: boolean };
};

const DEFAULTS: Settings = {
  theme: 'system',
  density: 'comfortable',
  layout: 'grid',
  reduce_motion: false,
  default_sort: 'distance',
  radius_km: 10,
  platforms: ['TikTok','Instagram','YouTube','Facebook'],
  cuisines: [],
  dietary_filters: [],
  language: 'es',
  map_style: 'standard',
  directions_pref: 'google',
  default_visibility: 'private',
  share_options: { allow_comments: true, allow_collaborators: false, show_creator: true },
};

const KEY = 'fs:settings:v1';

// -- hard guardrails so junk input can't poison state
const THEMES = new Set(['system','light','dark','contrast']);
const DENSITIES = new Set(['comfortable','compact']);
const LAYOUTS = new Set(['grid','list','map']);
const SORTS = new Set(['distance','rating','recent','popular']);
const PLATFORMS = new Set(['TikTok','Instagram','YouTube','Facebook']);
const LANGS = new Set(['en','es']);
const MAP_STYLES = new Set(['standard','satellite']);
const DIRS = new Set(['google','apple','waze','uber']);
const clamp = (n: any, lo: number, hi: number) => isFinite(+n) ? Math.min(hi, Math.max(lo, +n)) : lo;
function asStringArray(x: any): string[] { return Array.isArray(x) ? x.map(String).filter(Boolean) : []; }
function sanitizeSettings(s: any): Settings {
  const p = (v: any[]) => asStringArray(v).filter(v => PLATFORMS.has(v));
  return {
    theme: THEMES.has(s?.theme) ? s.theme : DEFAULTS.theme,
    density: DENSITIES.has(s?.density) ? s.density : DEFAULTS.density,
    layout: LAYOUTS.has(s?.layout) ? s.layout : DEFAULTS.layout,
    reduce_motion: !!s?.reduce_motion,
    default_sort: SORTS.has(s?.default_sort) ? s.default_sort : DEFAULTS.default_sort,
    radius_km: clamp(s?.radius_km, 1, 100), // keep the query sane
    platforms: p(s?.platforms).length ? p(s?.platforms) : DEFAULTS.platforms,
    cuisines: asStringArray(s?.cuisines),
    dietary_filters: asStringArray(s?.dietary_filters),
    language: LANGS.has(s?.language) ? s.language : DEFAULTS.language,
    map_style: MAP_STYLES.has(s?.map_style) ? s.map_style : DEFAULTS.map_style,
    directions_pref: DIRS.has(s?.directions_pref) ? s.directions_pref : DEFAULTS.directions_pref,
    default_visibility: ['private','unlisted','public'].includes(s?.default_visibility) ? s.default_visibility : DEFAULTS.default_visibility,
    share_options: (s?.share_options && typeof s.share_options === 'object') ? s.share_options : DEFAULTS.share_options,
  };
}

function loadLocal(): Settings | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return sanitizeSettings({ ...DEFAULTS, ...parsed });
  } catch { return null; }
}

function saveLocal(s: Settings) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {}
}

async function upsertRemote(userId: string, s: Settings) {
  const payload = {
    user_id: userId,
    theme: s.theme,
    density: s.density,
    layout: s.layout,
    reduce_motion: s.reduce_motion,
    default_sort: s.default_sort,
    radius_km: s.radius_km,
    platforms: s.platforms,
    cuisines: s.cuisines,
    dietary_filters: s.dietary_filters,
    language: s.language,
    map_style: s.map_style,
    directions_pref: s.directions_pref,
    default_visibility: s.default_visibility,
    share_options: s.share_options,
  } as any;
  const { error } = await supabase.from('user_settings').upsert(payload, { onConflict: 'user_id' });
  if (error) throw error;
}

async function fetchRemote(userId: string): Promise<Settings | null> {
  const { data, error } = await supabase.from('user_settings').select('*', { count: 'none' }).eq('user_id', userId).maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  if (!data) return null;
  return sanitizeSettings({ ...DEFAULTS, ...data });
}

const Ctx = createContext<{ settings: Settings; setSettings: (s: Settings) => void; reset: () => void; importSettings: (json: string) => boolean; exportSettings: () => string }>({
  settings: DEFAULTS,
  setSettings: () => {},
  reset: () => {},
  importSettings: () => false,
  exportSettings: () => '{}',
});

export const useSettings = () => useContext(Ctx);

export const SettingsProvider: React.FC<{ userId?: string | null, children: React.ReactNode }> = ({ userId, children }) => {
  const [settings, setSettingsState] = useState<Settings>(loadLocal() ?? DEFAULTS);
  const lastSyncedRef = React.useRef<string>(JSON.stringify(settings));

  // persist locally
  useEffect(() => { saveLocal(settings); }, [settings]);

  // cross-tab sync: adopt changes from other tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY || !e.newValue) return;
      try { setSettingsState(sanitizeSettings(JSON.parse(e.newValue))); } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // initial remote load
  useEffect(() => {
    let mounted = true;
    if (!userId) return;
    fetchRemote(userId).then(async (remote) => {
      if (!mounted) return;
      if (remote) {
        setSettingsState(remote);
      } else {
        // No remote row yet; provision defaults/current locally cached settings
        try { await upsertRemote(userId, settings); } catch {}
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, [userId]);

  // throttle remote sync
  const syncRef = React.useRef<number | null>(null);
  useEffect(() => {
    if (!userId) return;
    if (syncRef.current) window.clearTimeout(syncRef.current);
    syncRef.current = window.setTimeout(() => {
      const snap = JSON.stringify(settings);
      if (snap === lastSyncedRef.current) return; // skip identical writes
      upsertRemote(userId, settings).then(() => {
        lastSyncedRef.current = snap;
      }).catch(() => {});
    }, 800);
  }, [settings, userId]);

  const setSettings = useCallback((s: Settings) => setSettingsState(s), []);
  const reset = useCallback(() => setSettingsState(DEFAULTS), []);
  const importSettings = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json);
      setSettingsState(sanitizeSettings({ ...DEFAULTS, ...parsed }));
      return true;
    } catch { return false; }
  }, []);
  const exportSettings = useCallback(() => JSON.stringify(settings, null, 2), [settings]);

  // apply theme + density + reduced motion (system-aware)
  useEffect(() => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const isDark = settings.theme === 'dark' || (settings.theme === 'system' && prefersDark) || settings.theme === 'contrast';
    root.classList.toggle('dark', isDark);
    root.classList.toggle('contrast', settings.theme === 'contrast');
    root.classList.toggle('compact', settings.density === 'compact');
    root.classList.toggle('reduce-motion', !!settings.reduce_motion || prefersReduce);
  }, [settings.theme, settings.density, settings.reduce_motion]);

  // react to system theme changes when in 'system' mode
  useEffect(() => {
    if (settings.theme !== 'system') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => setSettingsState(s => ({ ...s })); // nudge to recompute classes
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [settings.theme]);

  const value = useMemo(() => ({ settings, setSettings, reset, importSettings, exportSettings }), [settings, setSettings, reset, importSettings, exportSettings]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
